/** One conversation comment, clipped to about four lines until expanded. */

import { useLayoutEffect, useRef, useState } from 'react';
import { formatInstant } from '@ampersandbrands/ui';
import type { SdStaffComment } from '@/lib/serviceDeskTypes';
import { AttachmentList } from './TicketTimeline';
import { SD_MUTED } from './sdStyles';

/** About four lines of body text. */
const CLIP_PX = 88;

export function TimelineComment({
  c,
  expanded,
  onToggle,
}: {
  c: SdStaffComment;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [overflows, setOverflows] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (el) setOverflows(el.scrollHeight > CLIP_PX + 4);
  }, [c.body_html]);
  const clipped = overflows && !expanded;
  const internal = c.visibility === 'internal';
  return (
    <li
      className={`rounded-lg border p-3 ${
        internal
          ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20'
          : 'border-amp-line bg-amp-white dark:border-amp-dark-line dark:bg-amp-dark-surface'
      }`}
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-2 text-[13px]">
        <span className="font-semibold text-amp-ink dark:text-amp-dark-ink">{c.author_name ?? 'Unknown'}</span>
        {c.author_is_staff && <span className={`text-[12px] ${SD_MUTED}`}>IT</span>}
        {internal && (
          <span className="rounded-pill bg-amber-200 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-caps-pill text-amber-950 dark:bg-amber-800/60 dark:text-amber-100">
            Internal note
          </span>
        )}
        <span className={`ml-auto text-[12px] ${SD_MUTED}`}>{formatInstant(c.created_at)}</span>
      </div>
      {/* Html sanitized by the api on write. */}
      <div
        ref={bodyRef}
        style={clipped ? { maxHeight: CLIP_PX, overflow: 'hidden' } : undefined}
        className="amp-rich-text text-sm text-amp-ink dark:text-amp-dark-ink"
        dangerouslySetInnerHTML={{ __html: c.body_html }}
      />
      {overflows && (
        <button
          type="button"
          className="mt-1 text-[12px] font-semibold text-amp-olive hover:underline dark:text-amp-olive-dark"
          onClick={onToggle}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
      <AttachmentList items={c.attachments} />
      {internal && c.mentions && c.mentions.length > 0 && (
        <p className={`mt-2 text-[12px] ${SD_MUTED}`}>
          Mentioned: {c.mentions.map((m) => m.name ?? m.email).join(', ')}
        </p>
      )}
    </li>
  );
}
